import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDropzone } from "react-dropzone"
import { getTintVTOService } from "services/tintvto"
import { downloadFileFromUrl } from "utils/fileReader"
import { fileToBase64 } from "utils/logicWithFiles"
import { Button, IconButton } from "@strapi/design-system"
import { customDefaultHeaders, ProductFields } from "."
import { useQueryClient } from "@tanstack/react-query"
import { TrashIcon } from "lucide-react"
import { useAppSelector } from "@/hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"

interface UploadBulkProductsProps {
  onAlert: (data: { error: boolean; text: string | React.ReactNode }) => void
  backHandler: () => void
  onClose: () => void
}

export const UploadBulkProducts: React.FC<UploadBulkProductsProps> = ({
  onAlert,
  backHandler,
  onClose,
}) => {
  const { t } = useTranslation()
  const [csvHeadersWithKeys, setCsvHeadersWithKeys] =
    useState<{ [key in ProductFields]?: string }>(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [isCsvValid, setIsCsvValid] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [headerValidationErrors, setHeaderValidationErrors] = useState<string[]>([])
  const [backendErrors, setBackendErrors] = useState<string | object>(null)
  const [isUploading, setIsUploading] = useState(false)
  const service = getTintVTOService()
  const queryClient = useQueryClient()
  const userInfo = useAppSelector(selectUserInfo)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return

      const reader = new FileReader()
      reader.readAsText(file)
      reader.onload = () => {
        const content = reader.result?.toString()
        if (!content) return

        const rows = content
          .split("\n")
          .map((line) => line.split(",").map((header) => header.trim().replace(/^"|"$/g, "")))
        const newHeaders = rows[0]
        setCsvFile(file)
        validateHeaders(newHeaders)
      }
      reader.onerror = () => {
        onAlert({
          error: true,
          text: `${t("uploadBulkProducts.csvError")} ${reader.error?.message}`,
        })
      }
    },
    [onAlert, t]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
  })

  const validateHeaders = useCallback(
    (fileHeaders: string[]) => {
      const newHeadersWithKeys: { [key in ProductFields]?: string } = {}
      const errors: string[] = []
      Object.keys(customDefaultHeaders).forEach((key) => {
        const field = key as ProductFields
        const { key: expectedHeader, required } = customDefaultHeaders[field]
        const headerIndex = fileHeaders.findIndex(
          (header) => header.trim().toLowerCase() === expectedHeader.trim().toLowerCase()
        )
        if (headerIndex !== -1) newHeadersWithKeys[field] = fileHeaders[headerIndex]
        else if (required)
          errors.push(t("uploadBulkProducts.expectedHeaderIsMissing", { expectedHeader }))
      })

      setIsCsvValid(errors.length === 0)
      setHeaderValidationErrors(errors)
      if (errors.length === 0) setCsvHeadersWithKeys(newHeadersWithKeys)
    },
    [t]
  )

  const handleUpload = async () => {
    if (csvFile) {
      setIsUploading(true)
      const baseFile = (await fileToBase64(csvFile)).replace("data:text/csv;base64,", "")
      try {
        const response = await service.uploadBulk(
          baseFile,
          window.btoa(JSON.stringify(csvHeadersWithKeys))
        )
        const data = response?.data
        onAlert({
          error: !data.uploadBulkNative,
          text: t("uploadBulkProducts.csvUploadSuccess"),
        })
        queryClient.invalidateQueries({ queryKey: ["products"] })
        onClose()
      } catch (error) {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const errorMessage = JSON.parse(error.graphQLErrors[0].errorInfo)
          setBackendErrors(errorMessage)
        } else if (error.networkError) {
          setBackendErrors(t("uploadBulkProducts.networkError"))
        } else {
          setBackendErrors(t("uploadBulkProducts.uploadError"))
        }
      } finally {
        setIsUploading(false) // Set loading state to false
      }
    }
  }

  const downloadExampleCsvHandler = useCallback(async () => {
    setIsDownloading(true)
    if (userInfo.groups.includes("Admin"))
      await downloadFileFromUrl("/assets/bulk_sample_admin.csv", "Admin_Custom_Sample_Sheet.csv")
    else await downloadFileFromUrl("/assets/bulk_sample.csv", "Custom_Sample_Sheet.csv")

    setTimeout(() => setIsDownloading(false), 2000)
  }, [])

  return (
    <div className="!p-4 !text-[#ffffff]">
      <div className="!flex !justify-center">
        <div className="  !my-6 !mx-5 !w-[510px] !border-1 !border-gray-600 !rounded-lg ">
          <div className="!m-4 !p-8 !text-center  justify-items-center">
            <p className=" !text-sm !leading-[19.5px]">
              {t("uploadBulkProducts.saveTimeByAddingProducts")}
            </p>
            <div className="!flex !justify-center !items-center !mt-2">
              <button
                className=" !font-bold !text-sm !leading-[18.5px] !cursor-pointer !flex !items-center !underline"
                onClick={!isDownloading ? downloadExampleCsvHandler : undefined}
                disabled={isDownloading}
              >
                <img src="/download.svg" alt="Download" className="!w-[13px] !h-[13px] !mr-1" />
                {isDownloading
                  ? t("uploadBulkProducts.downloading")
                  : t("uploadBulkProducts.downloadTheTemplate")}
              </button>
              <p className=" !text-sm !leading-[19.5px] !ml-1">
                {t("uploadBulkProducts.toRepeatCorrectFormat")}
              </p>
            </div>

            <div
              {...getRootProps()}
              className={`!mt-4 !p-6 !border-2 !border-dashed !rounded-lg ${
                isDragActive ? "!border-[#4542E0]" : "!border-[#DCDCE3]"
              }`}
            >
              <input {...getInputProps()} />
              <p className=" !text-sm">
                {isDragActive
                  ? t("uploadBulkProducts.dropFileHere")
                  : csvFile
                  ? t("uploadBulkProducts.dragAndDropOverwrite")
                  : t("uploadBulkProducts.dragAndDropSelect")}
              </p>
            </div>

            {csvFile && (
              <div
                className={`!mt-4 !mb-2 flex self-center max-w-72 gap-2 items-center justify-between !border !p-2 rounded ${
                  backendErrors || headerValidationErrors.length > 0
                    ? "!border-red-500"
                    : "!border-gray-500"
                }`}
              >
                <span className="text-sm font-medium truncate ">{csvFile.name}</span>
                <IconButton
                  onClick={() => {
                    setCsvFile(null)
                    setHeaderValidationErrors([])
                    setBackendErrors(null)
                  }}
                  icon={<TrashIcon />}
                />
              </div>
            )}

            {headerValidationErrors.length > 0 && (
              <div className="!mt-4 !text-red-500">
                {headerValidationErrors.map((error, index) => (
                  <p key={index} className="!text-xs">
                    {error}
                  </p>
                ))}
              </div>
            )}

            {backendErrors && (
              <span className="!text-sm !mt-4 !text-red-500 ">
                <span>{t("uploadBulkProducts.somethingIsWrong")}</span>
                <span
                  className="cursor-pointer underline"
                  onClick={() => {
                    // Convert object to string if it's an object
                    const content =
                      typeof backendErrors === "object"
                        ? JSON.stringify(backendErrors, null, 2)
                        : backendErrors

                    // Create blob and download
                    const blob = new Blob([content], { type: "text/plain" })
                    const url = window.URL.createObjectURL(blob)
                    const link = document.createElement("a")
                    link.href = url
                    link.download = "error.txt"
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(url)
                  }}
                >
                  {t("uploadBulkProducts.downloadError")}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="!flex !justify-between !gap-2 !px-[100px]">
        <Button onClick={backHandler} variant="tertiary">
          {t("uploadBulkProducts.back")}
        </Button>
        <Button onClick={handleUpload} disabled={!isCsvValid || isUploading}>
          {isUploading ? t("uploadBulkProducts.uploading") : t("uploadBulkProducts.uploadBulk")}
        </Button>
      </div>
    </div>
  )
}
